import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronDown } from "lucide-react";
import api from "@/lib/api";

// Short DPDP notice shown before personal data is collected. Single source: GET /api/privacy/notice.
export const PrivacyNotice = ({ compact = true }) => {
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(!compact);
  useEffect(() => { api.get("/privacy/notice").then((r) => setNotice(r.data)).catch(() => {}); }, []);
  if (!notice) return null;
  return (
    <div data-testid="privacy-notice" className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs text-slate-300">
      <button type="button" data-testid="privacy-notice-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-2 text-left">
        <span className="flex items-center gap-2 font-semibold text-cyan-300"><ShieldCheck className="h-4 w-4" /> Privacy Notice (v{notice.version}) — what we collect and why</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <ul className="space-y-1.5">
            {notice.items.map((it) => (
              <li key={it.data} className="flex gap-2"><span className="text-cyan-400">•</span>
                <span><b className="text-white">{it.data}</b>{it.required ? "" : " (optional)"} → {it.why}{it.ai ? " · processed by AI" : ""}</span></li>
            ))}
          </ul>
          <p><b className="text-white">AI processing:</b> {notice.ai_processing}</p>
          <p><b className="text-white">Third parties:</b> {notice.processors}</p>
          <p><b className="text-white">Your rights:</b> {notice.your_rights}</p>
          <p><b className="text-white">Grievance:</b> {notice.grievance}</p>
          <p>Full <Link to="/privacy" target="_blank" className="text-cyan-400 underline">Privacy Policy</Link> · <Link to="/terms" target="_blank" className="text-cyan-400 underline">Terms</Link></p>
        </div>
      )}
    </div>
  );
};

// Shared consent checkboxes: required acceptance (pre-ticked but un-tickable) + optional marketing (OFF by default).
export const ConsentBoxes = ({ accept, setAccept, marketing, setMarketing, showMarketing = true, acceptLabel }) => (
  <div className="space-y-2 text-xs text-slate-300">
    <label className="flex items-start gap-2">
      <input data-testid="accept-required-checkbox" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 accent-cyan-400" />
      <span>{acceptLabel || <>I have read and agree to the <Link to="/privacy" target="_blank" className="text-cyan-400 underline">Privacy Notice</Link> and <Link to="/terms" target="_blank" className="text-cyan-400 underline">Terms</Link> required to create my account. <span className="text-slate-500">(Required)</span></>}</span>
    </label>
    {!accept && <p data-testid="accept-required-error" className="text-rose-400">You cannot create an account without accepting the required Privacy Notice and Terms.</p>}
    {showMarketing && (
      <label className="flex items-start gap-2">
        <input data-testid="marketing-optin-checkbox" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 accent-cyan-400" />
        <span>I agree to receive promotional emails / WhatsApp / SMS about offers and new features. <span className="text-slate-500">(Optional — you can withdraw any time)</span></span>
      </label>
    )}
  </div>
);
